import type { QueryFunctionContext } from '@tanstack/react-query';
import axios from 'axios';
import type { CreateSitePayload, Site, Theme } from '../utils/types';


axios.interceptors.response.use((response) => {
  if (response.status === 403) {
    window.location.href = '/login';
  }
  return response;
});

const url = '/site';
const VENDORS_URL = '/vendors/pCode/';

export async function getSites(context: QueryFunctionContext): Promise<Site[]> {
  const [, queryPayload] = context.queryKey;
  const { pCode } = queryPayload as { pCode: string };

  const URL = `/customization/v2/site?pCode=${pCode}&sort=url,ASC`;

  const data = await axios.get(URL);
  return data?.data;
}

export async function getPremiumSites(context: QueryFunctionContext): Promise<Site[]> {
  const [, queryPayload] = context.queryKey;
  const { pCode } = queryPayload as { pCode: string };

  const URL = `/customization/v2/site?pCode=${pCode}&sort=url,ASC`;

  const data = await axios.get(URL);
  return data?.data?.filter((site: Site) => site.subscriptionId || site.premiumFeaturesIsActiveSspUser);
}

export async function getSiteById(
  context: QueryFunctionContext,
): Promise<Site> {
  const [, id] = context.queryKey;
  const data = await axios.get(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/site/${id}`,
  );
  return data?.data;
}

export async function getSiteByIdWithoutContext(id: string): Promise<Site> {
  const data = await axios.get(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/site/${id}`,
  );
  return data?.data;
}

export function createSite(data: CreateSitePayload) {
  if (data.propertyType === 'APP') {
    return axios.post(
      `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/app`,
      data,
    );
  } else {
    return axios.post(
      `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/site`,
      data,
    );
  }
}

export async function deleteSite({
  propertyId,
  type,
}: {
  propertyId: string;
  type: 'SITE' | 'APP';
}): Promise<void> {
  let url = '';
  if (type === 'SITE') {
    url = 'site';
  } else if (type === 'APP') {
    url = 'app';
  }
  await axios.delete(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL
    }/${url}/${propertyId}`,
  );
}

export function editSite([id, data]: [string, CreateSitePayload]) {
  const cleanedData = { ...data };

  return axios.put(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/site/${id}`,
    cleanedData,
  );
}

export async function getAnalyticsV2(
  pCode: string,
  pageSiteIds: string[],
): Promise<any> {
  if (!pCode) {
    return Promise.resolve({ res: undefined });
  }
  return axios.post(
    `/analytics/v2/metric/pcode/${pCode}/siteIds`,
    pageSiteIds,
    {
      withCredentials: true,
    },
  );
}

// APPS
export function createApp([data]: [any]) {
  return axios.post(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/app`,
    data,
  );
}

export function editApp([id, data]: [string, any]) {
  return axios.put(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/app/${id}`,
    data,
  );
}

export async function getThemes(
  context: QueryFunctionContext,
): Promise<Theme[]> {
  const url = '/customization/v2/theme';
  const [, queryPayload] = context.queryKey;
  const { pCode, themeType } = queryPayload as {
    pCode: string;
    themeType?: string;
  };
  const queryParams = new URLSearchParams();
  queryParams.append('pCode', pCode);
  if (themeType) {
    queryParams.append('themeType', themeType);
  }
  queryParams.append('sort', 'createdOn,ASC');
  const data = await axios.get(`${url}?${queryParams.toString()}`);
  return data?.data;
}

// Utils
export async function getCmpVersions(
  context: QueryFunctionContext,
): Promise<any> {
  const [, pCode] = context.queryKey;
  const data = await fetch(
    `https://api.cmp.inmobi.com/cmp-version-list?pCode=${pCode}`,
    {
      method: 'GET',
    },
  );
  return data.json();
}

export async function getV2_2Purposes(pCode: string): Promise<any> {
  console.log('pCode', pCode);
  const data = await axios.get(
    `${import.meta.env.VITE_CHOICE_2_V3_CUSTOMIZATION_API_URL
    }${VENDORS_URL}${pCode}`,
  );
  return data?.data;
}

export async function getGbcPurposes() {
  const data = await fetch(
    `https://cmp.inmobi.com/google-basic-consent/v1/purposes-en.json`,
    {
      method: 'GET',
    },
  );
  return data.json();
}

export async function getMSPAPurposes() {
  const url = `https://cmp.inmobi.com/us-mspa/v1/purposes-national-en.json`;
  const data = await axios.get(url);
  return data?.data;
}

export async function getV2_2PURPOSES(pCode: string): Promise<any> {
  const data = await axios.get(
    `${import.meta.env.VITE_CHOICE_2_V3_CUSTOMIZATION_API_URL
    }${VENDORS_URL}${pCode}`,
  );
  return data.data;
}

export async function getV2_0PURPOSES(pCode: string): Promise<any> {
  const data = await axios.get(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL
    }${VENDORS_URL}${pCode}`,
  );
  return data.data;
}

export async function createTheme(data: any) {
  const response = await axios.post(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/theme`,
    data,
  );
  return response.data;
}

export async function editTheme(id: string, data: any) {
  const response = await axios.put(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/theme/${id}`,
    data,
  );
  return response.data;
}

export async function getThemeById(id: string) {
  const response = await axios.get(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/theme/${id}`,
  );
  return response.data;
}

export async function deleteTheme(id: string) {
  const response = await axios.delete(
    `${import.meta.env.VITE_CHOICE_2_V2_CUSTOMIZATION_API_URL}/theme/${id}`,
  );
  return response.data;
}

export function downloadFile(
  url: string,
  fileName: string,
  onProgress?: (progress: number) => void,
) {
  return axios({
    url,
    method: 'GET',
    responseType: 'blob',
    onDownloadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      }
    },
  })
    .then((response) => {
      // Get the content-disposition header from the response
      const contentDisposition = response.headers['content-disposition'];

      // Extract the filename from the content-disposition header using regex
      const matches =
        contentDisposition && contentDisposition.match(/filename="(.+)"/);
      let downloadedFileName = fileName; // Use the provided filename as a fallback

      if (matches && matches.length > 1) {
        downloadedFileName = matches[1];
      }

      // Create the blob URL and initiate download with the extracted filename
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadedFileName; // Set the filename for download
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
    .catch((err) => ({ err }));
}
export async function bulkUpdateSites(
  pCode: string,
  payload: {
    siteUpdatableFields: Record<string, any>;
    siteIds: number[];
  },
) {
  const url = `/customization/bulk-update/pcode/${pCode}`;
  const data = await axios.put(url, payload);
  return data?.data;
}

export async function previewTheme(data: any) {
  const response = await axios.post(
    `/builder/v2/choicejs/preview`,
    data,
  );
  return response.data;
}